<template>
  <div class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 h-[400px] flex flex-col relative overflow-hidden">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6 flex-shrink-0">
      <!-- Title with Icon -->
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 dark:from-purple-500 dark:to-indigo-600
                    flex items-center justify-center shadow-lg">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100">
          {{ directT('dashboard.crm.balanceOverview') }}
        </h3>
      </div>

      <!-- Period Selector -->
      <div class="relative">
        <button
          type="button"
          @click.stop="toggleActive"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600
                 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-600
                 text-slate-700 dark:text-slate-200 transition-all duration-300 shadow-sm
                 hover:shadow-md focus:ring-2 focus:ring-violet-500/20"
          :class="{ 'ring-2 ring-violet-500/20': isActive }"
        >
          <span>{{ getPeriodLabel() }}</span>
          <svg class="w-4 h-4 transition-transform duration-200"
               :class="{ 'rotate-180': isActive }"
               fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>

        <!-- Dropdown Menu -->
        <div
          v-if="isActive"
          class="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-[#0c1427] border border-gray-200 dark:border-[#172036] rounded-md shadow-lg z-10"
        >
          <button
            v-for="period in [
              { key: 'week', label: directT('dashboard.thisWeek') },
              { key: 'month', label: directT('dashboard.thisMonth') },
              { key: 'year', label: directT('dashboard.thisYear') }
            ]"
            :key="period.key"
            type="button"
            @click="setPeriod(period.key as any)"
            class="w-full text-left px-4 py-2.5 text-sm font-medium
                   text-slate-700 dark:text-slate-200 hover:bg-violet-50
                   dark:hover:bg-violet-900/20 transition-colors duration-200
                   flex items-center gap-2"
            :class="{ 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300': selectedPeriod === period.key }"
          >
            <div class="w-2 h-2 rounded-full"
                 :class="selectedPeriod === period.key ? 'bg-violet-500' : 'bg-transparent'"></div>
            {{ period.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Chart Section -->
    <div class="flex-1 min-h-0 relative w-full overflow-hidden">
      <div v-if="loading" class="flex justify-center items-center h-full w-full absolute inset-0">
        <div class="flex flex-col items-center gap-4">
          <div class="w-16 h-16 border-4 border-violet-200 dark:border-violet-800
                      border-t-violet-500 dark:border-t-violet-400 rounded-full animate-spin"></div>
          <span class="text-sm font-medium text-slate-600 dark:text-slate-400">
            {{ directT('general.loading') }}...
          </span>
        </div>
      </div>
      <div v-else class="w-full h-full relative">
        <apexchart
          v-if="isClient"
          type="area"
          height="280"
          class="w-full"
          :options="balanceOverview"
          :series="overview"
        ></apexchart>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import { supabase } from "@/lib/supabaseClient";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Define interfaces for type safety
interface Organization {
  amountpaid: number | null;
  created_at: string;
}

// Types for time periods
type TimePeriod = 'week' | 'month' | 'year';

export default defineComponent({
  name: "BalanceOverviewChart",
  setup() {
    const { locale } = useI18n();
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const isClient = ref(false);
    const loading = ref(true);
    const currencySymbol = ref('₪'); // קבוע - סמל השקל
    const selectedPeriod = ref<TimePeriod>('year');
    const totalRevenue = ref(0);
    const periodDataRevenue = ref<number[]>([]);

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];

      for (const part of parts) {
        if (!result || !(part in result)) {
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    const overview = computed(() => {
      return [
        {
          name: directT('dashboard.revenue'),
          data: periodDataRevenue.value,
        }
      ];
    });

    // Computed for x-axis categories based on selected period
    const getCategories = computed(() => {
      const today = new Date();

      if (selectedPeriod.value === 'week') {
        // Return days of the week
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          days.push(date.toLocaleString(locale.value, { weekday: 'short' }));
        }
        return days;
      } else if (selectedPeriod.value === 'month') {
        // Return days of the month (current month)
        const days = [];
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          days.push(i.toString());
        }
        return days;
      } else {
        // Return months of the year
        const months = [];
        for (let i = 0; i < 12; i++) {
          const date = new Date(today.getFullYear(), i, 1);
          months.push(date.toLocaleString(locale.value, { month: 'short' }));
        }
        return months;
      }
    });

    // Balance chart options
    const balanceOverview = computed(() => {
      return {
        chart: {
          type: "area",
          height: 280,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 1200,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 400
            }
          },
          background: 'transparent',
          dropShadow: {
            enabled: true,
            color: '#AD63F6',
            top: 8,
            left: 0,
            blur: 15,
            opacity: 0.15
          }
        },
        colors: ["#AD63F6"],
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.4,
            gradientToColors: ["#4F46E5", "#7C3AED"],
            inverseColors: false,
            opacityFrom: 0.8,
            opacityTo: 0.1,
            stops: [0, 50, 100],
            colorStops: [
              {
                offset: 0,
                color: "#AD63F6",
                opacity: 0.9
              },
              {
                offset: 50,
                color: "#7C3AED",
                opacity: 0.6
              },
              {
                offset: 100,
                color: "#4F46E5",
                opacity: 0.1
              }
            ]
          }
        },
        dataLabels: {
          enabled: false,
        },
        grid: {
          show: true,
          borderColor: "rgba(148, 163, 184, 0.08)",
          strokeDashArray: 3,
          position: 'back',
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          },
          row: {
            colors: undefined,
            opacity: 0.5
          },
          column: {
            colors: undefined,
            opacity: 0.5
          },
          padding: {
            top: 0,
            right: 10,
            bottom: 5,
            left: 5
          }
        },
        stroke: {
          curve: "smooth",
          width: 3,
          lineCap: 'round',
          dashArray: 0,
          colors: ["#AD63F6"]
        },
        markers: {
          size: 0,
          colors: ["#AD63F6"],
          strokeColors: "#ffffff",
          strokeWidth: 3,
          strokeOpacity: 0.9,
          strokeDashArray: 0,
          fillOpacity: 1,
          discrete: [],
          shape: "circle",
          radius: 6,
          offsetX: 0,
          offsetY: 0,
          hover: {
            size: 8,
            sizeOffset: 3
          }
        },
        xaxis: {
          categories: getCategories.value,
          axisTicks: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          labels: {
            show: true,
            style: {
              colors: "#94A3B8",
              fontSize: "12px",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 500,
            },
            offsetY: 5,
          },
          tooltip: {
            enabled: false
          }
        },
        yaxis: {
          tickAmount: 5,
          min: 0,
          labels: {
            formatter: (val: string) => {
              const numVal = Number(val);
              if (numVal >= 1000000) {
                return currencySymbol.value + (numVal / 1000000).toFixed(1) + 'M';
              } else if (numVal >= 1000) {
                return currencySymbol.value + (numVal / 1000).toFixed(1) + 'K';
              }
              return currencySymbol.value + numVal.toLocaleString();
            },
            style: {
              colors: "#64748B",
              fontSize: "11px",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 500,
            },
            offsetX: -5,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          enabled: true,
          theme: "light",
          style: {
            fontSize: "12px",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          },
          custom: function({series, seriesIndex, dataPointIndex, w}: any) {
            const value = series[seriesIndex][dataPointIndex];
            const category = w.globals.labels[dataPointIndex];

            return `
              <div class="custom-tooltip" style="
                background: rgba(255, 255, 255, 0.95);
                backdrop-filter: blur(20px);
                border: 1px solid rgba(173, 99, 246, 0.2);
                border-radius: 12px;
                padding: 12px 16px;
                box-shadow: 0 10px 40px rgba(173, 99, 246, 0.15);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              ">
                <div style="
                  font-size: 11px;
                  color: #64748B;
                  font-weight: 500;
                  margin-bottom: 4px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                ">${category}</div>
                <div style="
                  font-size: 16px;
                  font-weight: 700;
                  color: #1E293B;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                ">
                  <div style="
                    width: 8px;
                    height: 8px;
                    background: linear-gradient(135deg, #AD63F6, #7C3AED);
                    border-radius: 50%;
                  "></div>
                  ${currencySymbol.value}${value.toLocaleString()}
                </div>
              </div>
            `;
          },
          x: {
            show: false,
          },
          y: {
            formatter: (val: number) => {
              return currencySymbol.value + val.toLocaleString();
            }
          },
          marker: {
            show: false,
          }
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.04,
            }
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'darken',
              value: 0.7,
            }
          }
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: "100%"
              },
              legend: {
                position: "bottom",
                offsetY: 5
              },
              grid: {
                padding: {
                  top: 5,
                  right: 10,
                  bottom: 5,
                  left: 10
                }
              }
            }
          }
        ]
      };
    });

    // Get label for current period
    const getPeriodLabel = () => {
      if (selectedPeriod.value === 'week') {
        return directT('dashboard.thisWeek');
      } else if (selectedPeriod.value === 'month') {
        return directT('dashboard.thisMonth');
      } else {
        return directT('dashboard.thisYear');
      }
    };

    // Set period and fetch data
    const setPeriod = (period: TimePeriod) => {
      selectedPeriod.value = period;
      fetchOrganizationsData();
      toggleActive();
    };

    // Get date range based on selected period
    const getDateRange = () => {
      const today = new Date();
      let startDate = new Date();

      if (selectedPeriod.value === 'week') {
        // Last 7 days
        startDate.setDate(today.getDate() - 6);
      } else if (selectedPeriod.value === 'month') {
        // Current month
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      } else {
        // Current year
        startDate = new Date(today.getFullYear(), 0, 1);
      }

      return { startDate, endDate: today };
    };

    // Fetch organizations data from Supabase
    const fetchOrganizationsData = async () => {
      try {
        loading.value = true;

        // קביעת סמל המטבע לשקל
        currencySymbol.value = '₪';

        // Get date range for fetching organizations
        const { startDate, endDate } = getDateRange();

        // Fetch organizations for the selected period
        const { data: organizationsData, error } = await supabase
          .from('organizations')
          .select('amountpaid, created_at')
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .not('amountpaid', 'is', null)
          .order('created_at', { ascending: true });

        if (error) {
          loading.value = false;
          return;
        }

        if (organizationsData && organizationsData.length > 0) {
          console.log('🔍 BalanceOverviewChart: דוגמאות לנתונים:',
            organizationsData.slice(0, 3).map(item => ({
              amountpaid: item.amountpaid,
              created_at: item.created_at
            }))
          );
        }

        // Process data based on the selected period
        processData(organizationsData as Organization[]);

      } catch (error) {
        console.error('Error fetching organizations data:', error);
      } finally {
        loading.value = false;
      }
    };

    // Process the data based on selected period
    const processData = (organizationsData: Organization[]) => {
      // Reset totals
      totalRevenue.value = 0;

      const today = new Date();

      if (selectedPeriod.value === 'week') {
        // Process data for the week (last 7 days)
        const dailyRevenue = Array(7).fill(0);

        organizationsData.forEach(org => {
          if (!org.amountpaid) return;

          const amount = Number(org.amountpaid);
          if (isNaN(amount)) return;

          const orgDate = new Date(org.created_at);
          const diffDays = Math.floor((today.getTime() - orgDate.getTime()) / (1000 * 60 * 60 * 24));

          if (diffDays >= 0 && diffDays < 7) {
            const index = 6 - diffDays;
            totalRevenue.value += amount;
            dailyRevenue[index] += amount;
          }
        });

        periodDataRevenue.value = dailyRevenue;

      } else if (selectedPeriod.value === 'month') {
        // Process data for the month (current month)
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        const dailyRevenue = Array(daysInMonth).fill(0);

        organizationsData.forEach(org => {
          if (!org.amountpaid) return;

          const amount = Number(org.amountpaid);
          if (isNaN(amount)) return;

          const orgDate = new Date(org.created_at);

          // Check if org is from current month
          if (orgDate.getMonth() === today.getMonth() && orgDate.getFullYear() === today.getFullYear()) {
            const dayOfMonth = orgDate.getDate() - 1; // 0-based index
            totalRevenue.value += amount;
            dailyRevenue[dayOfMonth] += amount;
          }
        });

        periodDataRevenue.value = dailyRevenue;

      } else {
        // Process data for the year (current year)
        const monthlyRevenue = Array(12).fill(0);

        organizationsData.forEach(org => {
          if (!org.amountpaid) return;

          const amount = Number(org.amountpaid);
          if (isNaN(amount)) return;

          const orgDate = new Date(org.created_at);

          // Check if org is from current year
          if (orgDate.getFullYear() === today.getFullYear()) {
            const monthIndex = orgDate.getMonth();
            totalRevenue.value += amount;
            monthlyRevenue[monthIndex] += amount;
          }
        });

        periodDataRevenue.value = monthlyRevenue;
      }
    };

    onMounted(() => {
      isClient.value = true;
      fetchOrganizationsData();
    });

    // Watch for locale changes and refresh data
    watch(locale, () => {
      fetchOrganizationsData();
    });

    return {
      isClient,
      loading,
      overview,
      balanceOverview,
      isActive,
      toggleActive,
      totalRevenue,
      currencySymbol,
      selectedPeriod,
      setPeriod,
      getPeriodLabel,
      directT,
    };
  },
});
</script>
